import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import AppDropdown from '../../../components/AppDropdown'
import AppToggle from '../../../components/AppToggle'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import { useAdmissionFormSettingEntryScreen } from './hooks/useAdmissionFormSettingEntryScreen'

function SettingField({ enabled, mandatory, label, displayName, onToggle, onMandatoryToggle, onLabelChange }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition ${enabled ? 'border-outline-variant/40 bg-surface-container-lowest' : 'border-outline-variant/20 bg-surface-container-low opacity-55'}`}>
      <label className="flex items-center gap-2 min-w-0 shrink-0 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={enabled}
          onChange={onToggle}
          className="h-4 w-4 shrink-0 cursor-pointer rounded border-outline-variant/40 text-primary focus:ring-primary/20"
        />
        <span className="text-xs font-semibold text-on-surface whitespace-nowrap">{displayName}</span>
      </label>

      <input
        type="text"
        value={label}
        onChange={onLabelChange}
        disabled={!enabled}
        className="flex-1 min-w-0 h-8 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-2.5 text-xs font-semibold text-on-surface outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-surface-container-low disabled:text-on-surface-variant transition"
      />

      <button
        type="button"
        onClick={onMandatoryToggle}
        disabled={!enabled}
        className={`shrink-0 rounded-lg px-2.5 py-1 text-[10px] font-bold tracking-wide transition cursor-pointer disabled:cursor-default ${mandatory
            ? 'bg-error-container text-error border border-error/30'
            : 'bg-primary/10 text-primary border border-primary/30'
          } disabled:opacity-50`}
      >
        {mandatory ? 'Mandatory' : 'Optional'}
      </button>
    </div>
  )
}

const SECTIONS = [
  { title: 'Course Details', group: 'courseDetails', icon: 'school' },
  { title: 'Students Details', group: 'studentDetails', icon: 'person' },
  { title: 'Guardian Details', group: 'guardianDetails', icon: 'supervisor_account' },
  { title: 'Guardian1 Details', group: 'guardian1Details', icon: 'family_restroom' },
  { title: 'School / College Details', group: 'schoolCollegeDetails', icon: 'domain' },
]

export default function AdmissionFormSettingEntryScreen() {
  const { state, actions } = useAdmissionFormSettingEntryScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  if (state.isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] items-center justify-center rounded-2xl border border-outline-variant/25 bg-surface-container-lowest px-6 py-20 text-sm text-on-surface-variant shadow-sm">
        <span className="material-symbols-outlined animate-spin text-xl text-primary mr-3">progress_activity</span>
        Loading form setting details...
      </div>
    )
  }

  const tips = [
    'With WebEdify, you can create an Enquiry Form and allow students/parents to fill online forms for a student. This is also available in the student login area.',
    'The Enquiry form can be used from PC or mobile but we recommend that you use lost tablet.',
    'To customize the enquiry form as per your needs, please enable or disable desired fields and mark important fields as mandatory.',
    'You can also change default field name as per your choice.',
    'Logo can be used by WebEdify or other modules to show your brand identity.',
  ]

  const handleFieldToggle = (group, key, item) => {
    actions.updateSettingFlag(group, key, 'enabled', !item.enabled)
  }

  const handleMandatoryToggle = (group, key, item) => {
    actions.updateSettingFlag(group, key, 'mandatory', !item.mandatory)
  }

  const handleLabelChange = (group, key, value) => {
    actions.updateSettingFlag(group, key, 'label', value)
  }

  const companyOptions = (state.options.companyOptions || []).map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  )
  const branchOptions = (state.options.branchOptions || []).map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  )

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-28">
      {/* ── Header Title & Navigation ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            Admission Module
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {state.isEditMode ? 'Edit Form Setting' : 'New Admission Form Setting'}
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Configure enquiry form fields, labels, and mandatory settings.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/admission/admission-form-setting/list"
            className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            <span>Back to List</span>
          </Link>
        </div>
      </section>

      {/* ── Main Form Card ── */}
      <section className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-sm">
        <div className="border-b border-outline-variant/15 pb-4 mb-6 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-bold text-on-surface">
              Form Configuration
            </h2>
            <p className="text-xs text-on-surface-variant">
              Set company, name, and branch for this form setting.
            </p>
          </div>
          {state.isEditMode && (
            <span className="rounded-lg bg-primary/10 px-3 py-1 font-mono text-xs font-bold text-primary">
              Editing
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 items-start">
          {/* Company */}
          <div className="grid gap-1.5">
            <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
              Company<span className="text-error"> *</span>
            </label>
            <AppDropdown
              options={companyOptions}
              value={state.form.company}
              onChange={(value) => actions.patchField('company', value)}
              placeholder="Select Company"
              triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
            />
          </div>

          {/* Branch */}
          <div className="grid gap-1.5">
            <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
              Branch
            </label>
            <AppDropdown
              options={branchOptions}
              value={state.form.branch}
              onChange={(value) => actions.patchField('branch', value)}
              placeholder="Select Branch"
              triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
            />
          </div>

          {/* Form Name / URL */}
          <div className="grid gap-1.5">
            <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
              Form Name / URL<span className="text-error"> *</span>
            </label>
            <input
              type="text"
              value={state.form.name || ''}
              onChange={(e) => actions.patchField('name', e.target.value)}
              placeholder="Enter form setting name"
              className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-semibold text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
            />
          </div>

          {/* Status Toggle */}
          <div className="col-span-1 md:col-span-3 flex items-center gap-3 pt-2">
            <AppToggle
              checked={state.form.status === 'Active'}
              onChange={(checked) => actions.patchField('status', checked ? 'Active' : 'Inactive')}
              ariaLabel="Form setting status"
            />
            <span className="text-xs font-semibold text-on-surface select-none">
              {state.form.status === 'Active' ? 'Active' : 'Inactive'} — Form is {state.form.status === 'Active' ? 'visible' : 'hidden'} to students
            </span>
          </div>
        </div>
      </section>

      {/* ── Tips Sidebar & Fields Layout ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4 items-start">
        {/* Field Sections (3 cols) */}
        <div className="flex flex-col gap-6 lg:col-span-3">
          {SECTIONS.map((section) => {
            const fields = Object.entries(state.form.settings[section.group] || {})

            return (
              <section key={section.group} className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3.5">
                  <span className="material-symbols-outlined text-base text-primary">{section.icon}</span>
                  <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
                    {section.title}
                  </span>
                  <span className="ml-auto rounded-lg bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                    {fields.filter(([, item]) => item.enabled).length}/{fields.length} enabled
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
                  {fields.map(([key, item]) => (
                    <SettingField
                      key={`${section.group}-${key}`}
                      enabled={item.enabled}
                      mandatory={item.mandatory}
                      label={item.label}
                      displayName={item.label}
                      onToggle={() => handleFieldToggle(section.group, key, item)}
                      onMandatoryToggle={() => handleMandatoryToggle(section.group, key, item)}
                      onLabelChange={(e) => handleLabelChange(section.group, key, e.target.value)}
                    />
                  ))}
                </div>
              </section>
            )
          })}
        </div>

        {/* Tips Sidebar Card (1 col) */}
        <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-5 shadow-sm lg:sticky lg:top-24">
          <div className="flex items-center gap-2 border-b border-outline-variant/15 pb-3 mb-3">
            <span className="material-symbols-outlined text-base text-primary">lightbulb</span>
            <h3 className="text-xs font-bold uppercase tracking-wider text-on-surface">
              Enquiry Form Tips
            </h3>
          </div>
          <ul className="space-y-2.5 text-xs text-on-surface-variant">
            {tips.map((tip, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="material-symbols-outlined text-sm text-primary shrink-0 mt-0.5">
                  check_circle
                </span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Shared Footer Action Bar ── */}
      <AppFormFooterBar
        cancelPath="/admission/admission-form-setting/list"
        cancelLabel="Discard"
        saveLabel={state.isEditMode ? 'Update & Exit' : 'Save & Exit'}
        onSaveExit={actions.handleSave}
        isSaving={state.isSaving}
      />
    </div>
  )
}
